import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

import { BedStatus, RoomStatus } from '../../types/enums';
import {
  assignBedToPatient,
  dischargePatientFromBed,
  transferPatientToBed,
  getAvailableBeds,
  getCurrentBedAssignment
} from '../../services/bedAssignmentService';
import { getPatient } from '../../services/patientService';
import {
  assignPatientToRoom,
  getCurrentRoomAssignment,
  dischargePatientFromRoom,
  getAllAvailableRooms,
  isStandaloneRoom,
  transferPatientBetweenRooms
} from '../../services/roomAssignmentService';
import { useUserData } from '../../hooks/useUserData';

const BedAssignmentManager = ({ facilityId, patientId, encounterId, patient, encounter, onRefreshEncounter }) => {
  const { user } = useUserData();
  const [loading, setLoading] = useState(true);
  const [availableBeds, setAvailableBeds] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // UI state for loading during operations
  const [confirming, setConfirming] = useState(false);

  const loadBedData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Load patient data first
      if (!patient?.name || !patient?.mrn) {
        const patientData = await getPatient(patientId);
        if (!patientData) {
          setError('Failed to load patient data');
          return;
        }
      }

      const bedAssignment = await getCurrentBedAssignment(patientId);
      const roomAssignment = await getCurrentRoomAssignment(patientId);
      if (bedAssignment) {
        setCurrentAssignment({ ...bedAssignment, type: 'bed' });
      } else if (roomAssignment) {
        setCurrentAssignment({ ...roomAssignment, type: 'room' });
      } else {
        setCurrentAssignment(null);
      }
      let bedsResponse = await getAvailableBeds(facilityId);
     
      let rooms = await getAllAvailableRooms(facilityId);
      
      setAvailableBeds(bedsResponse?.availableBeds || []);
      setAvailableRooms(rooms);
    } catch (err) {
      setError('Failed to load assignment data: ' + err.message);
      // Debug: log error
      console.error('[BedAssignmentManager] loadBedData error:', err);
    } finally {
      setLoading(false);
    }
  }, [facilityId, patientId, patient]);

  // Load initial data
  useEffect(() => {
    if (facilityId && patientId && encounterId) {
      loadBedData();
    }
  }, [facilityId, patientId, encounterId, loadBedData]);

  // Early return after hooks if required props are missing
  if (!facilityId || !patientId || !encounterId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          Missing required information for bed assignment. Please ensure the encounter is fully loaded.
          <br />
          <small>
            FacilityId: {facilityId || 'Missing'} | 
            PatientId: {patientId || 'Missing'} | 
            EncounterId: {encounterId || 'Missing'}
          </small>
        </Alert>
      </Box>
    );
  }

  // Helper: get beds for a room
  const getBedsForRoom = (roomId) => availableBeds.filter(bed => bed.roomId === roomId);

  // Helper: generate readable bed display name
  const getBedDisplayName = (bed, room) => {
    // If bed has a proper name, use it
    if (bed.name && bed.name !== bed.bedId) {
      return bed.name;
    }
    
    // Try to generate a meaningful name based on room and bed info
    const roomName = room?.name || room?.code || room?.roomId || 'Room';
    const bedIndex = getBedsForRoom(bed.roomId).indexOf(bed) + 1;
    
    // If we have multiple beds in the room, use letters (A, B, C, etc.)
    const roomBeds = getBedsForRoom(bed.roomId);
    if (roomBeds.length > 1) {
      const bedLetter = String.fromCharCode(65 + bedIndex - 1); // A, B, C, etc.
      return `${roomName} Bed ${bedLetter}`;
    } else {
      return `${roomName} Bed`;
    }
  };

  // Helper: room status
  const getRoomStatus = (room) => {
    const beds = getBedsForRoom(room.roomId);
    const available = beds.filter(b => b.status === BedStatus.AVAILABLE).length;
    if (available === beds.length) return 'Available';
    if (available === 0) return 'Occupied';
    return 'Partial';
  };
  const getRoomStatusColor = (status) => {
    if (status === 'Available') return 'success';
    if (status === 'Partial') return 'warning';
    return 'error';
  };

  // Helper: bed status
  const getBedStatusColor = (status) => (status === BedStatus.AVAILABLE ? 'success' : 'default');

  // Assignment logic for beds and stand-alone rooms
  const handleAssignOrTransfer = async ({ room, bed }) => {
    setConfirming(true);
    setError('');
    
    // Validate required data before proceeding
    if (!encounterId) {
      setError('Cannot assign bed: Encounter ID is missing. Please refresh the page and try again.');
      setConfirming(false);
      return;
    }

    if (!encounter?.type) {
      setError('Cannot assign bed: Encounter type is missing. Please ensure the encounter is fully loaded.');
      setConfirming(false);
      return;
    }

    try {
      // If patient has any active assignment, handle transfers
      if (currentAssignment) {
        // Transfer to a specific bed
        if (bed) {
          if (currentAssignment.type === 'bed' && bed.bedId === currentAssignment.bedId) {
            setError('Patient is already assigned to this bed. Discharge or transfer to another bed to reassign.');
            return;
          }
          
          // Transfer from any assignment type to a bed
          if (currentAssignment.type === 'bed') {
            // Bed-to-bed transfer
            await transferPatientToBed(currentAssignment.id, bed.bedId, user.uid, user.role || 'nurse', 'Transferred via UI');
            setSuccess('Patient successfully transferred to new bed');
            if (onRefreshEncounter) onRefreshEncounter();
          } else {
            // Room-to-bed transfer: discharge from room and assign to bed
            await dischargePatientFromRoom(currentAssignment.id, user.uid, user.role || 'nurse', 'Transferred to bed');
            await assignBedToPatient({
              bedId: bed.bedId,
              facilityId,
              departmentId: room.departmentId || bed.departmentId,
              roomId: room.roomId,
              patientId,
              patientName: patient?.name || '',
              patientMRN: patient?.mrn || '',
              encounterId,
              encounterType: encounter?.type || 'inpatient',
              assignedBy: user.uid,
              assignedByRole: user.role || 'nurse',
              admittingDiagnosis: encounter?.admittingDiagnosis || '',
              attendingPhysician: encounter?.attendingPhysician || '',
              attendingPhysicianId: encounter?.attendingPhysicianId || '',
              specialInstructions: '',
              isolationFlags: [],
              fallRisk: false,
              bedAlerts: []
            });
            setSuccess('Patient successfully transferred from room to bed');
            if (onRefreshEncounter) onRefreshEncounter();
          }
        }
        // Transfer to a standalone room
        else if (room && isStandaloneRoom(room)) {
          if (currentAssignment.type === 'room' && room.roomId === currentAssignment.roomId) {
            setError('Patient is already assigned to this room. Discharge or transfer to another room to reassign.');
            return;
          }
          
          // Transfer from any assignment type to a room
          if (currentAssignment.type === 'room') {
            // Room-to-room transfer
            await transferPatientBetweenRooms(currentAssignment.id, room.roomId, user.uid, user.role || 'nurse', 'Transferred via UI');
            setSuccess('Patient successfully transferred to new room');
            if (onRefreshEncounter) onRefreshEncounter();
          } else {
            // Bed-to-room transfer: discharge from bed and assign to room
            await dischargePatientFromBed(currentAssignment.id, user.uid, user.role || 'nurse', 'Transferred to room');
            await assignPatientToRoom(
              room.roomId,
              patientId,
              encounterId,
              user.uid,
              user.role || 'nurse',
              facilityId
            );
            setSuccess('Patient successfully transferred from bed to room');
            if (onRefreshEncounter) onRefreshEncounter();
          }
        }
      } else {
        // No active assignment: assign new bed or room
        if (bed) {
          await assignBedToPatient({
            bedId: bed.bedId,
            facilityId,
            departmentId: room.departmentId || bed.departmentId,
            roomId: room.roomId,
            patientId,
            patientName: patient?.name || '',
            patientMRN: patient?.mrn || '',
            encounterId,
            encounterType: encounter?.type || 'inpatient',
            assignedBy: user.uid,
            assignedByRole: user.role || 'nurse',
            admittingDiagnosis: encounter?.admittingDiagnosis || '',
            attendingPhysician: encounter?.attendingPhysician || '',
            attendingPhysicianId: encounter?.attendingPhysicianId || '',
            specialInstructions: '',
            isolationFlags: [],
            fallRisk: false,
            bedAlerts: []
          });
          setSuccess('Patient successfully assigned to bed');
          if (onRefreshEncounter) onRefreshEncounter();
        } else if (room && isStandaloneRoom(room)) {
          await assignPatientToRoom(
            room.roomId,
            patientId,
            encounterId,
            user.uid,
            user.role || 'nurse',
            facilityId
          );
          setSuccess('Patient successfully assigned to room');
          if (onRefreshEncounter) onRefreshEncounter();
        }
      }
      loadBedData();
    } catch (err) {
      setError('Assignment failed: ' + err.message);
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (confirming) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Processing assignment...</Typography>
      </Box>
    );
  }

  // Group rooms and beds by department
  const departmentsMap = {};
  availableRooms.forEach(room => {
    const deptId = room.departmentId || room.department || 'Unknown';
    const deptName = room.departmentName || room.department || deptId;
    if (!departmentsMap[deptId]) {
      departmentsMap[deptId] = { rooms: [], departmentName: deptName };
    } else {
      // Update department name if we get a better one
      if (deptName !== 'Unknown' && departmentsMap[deptId].departmentName === 'Unknown') {
        departmentsMap[deptId].departmentName = deptName;
      }
    }
    departmentsMap[deptId].rooms.push(room);
  });
  
  // Also group beds by their department (for beds without rooms)
  availableBeds.forEach(bed => {
    const deptId = bed.departmentId || bed.department || 'Unknown';
    const deptName = bed.departmentName || bed.department || deptId;
    if (!departmentsMap[deptId]) {
      departmentsMap[deptId] = { rooms: [], departmentName: deptName };
    } else {
      // Update department name if we get a better one
      if (deptName !== 'Unknown' && departmentsMap[deptId].departmentName === 'Unknown') {
        departmentsMap[deptId].departmentName = deptName;
      }
    }
  });

  // Map beds by roomId for quick lookup
  const bedsByRoom = {};
  availableBeds.forEach(bed => {
    if (!bedsByRoom[bed.roomId]) bedsByRoom[bed.roomId] = [];
    bedsByRoom[bed.roomId].push(bed);
  });

  // Build a departmentId -> departmentName map from both rooms and beds
  const departmentIdNameMap = {};
  [...availableRooms, ...availableBeds].forEach(item => {
    if (item.departmentId && item.departmentName && item.departmentName !== 'Unknown') {
      departmentIdNameMap[item.departmentId] = item.departmentName;
    }
  });

  // Show a message if the patient is already assigned and not transferring
  const isAssigned = !!currentAssignment;

  return (
    <Box>
      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {isAssigned && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Patient already has an active {currentAssignment.type}. To reassign, select a different {currentAssignment.type === 'bed' ? 'bed' : 'room'}.
        </Alert>
      )}

      {/* Hierarchical Tabular View: Departments -> Rooms -> Beds */}
      {Object.entries(departmentsMap).map(([deptId, dept]) => (
        <Box key={deptId} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            {departmentIdNameMap[deptId] || dept.departmentName || deptId}
          </Typography>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table size="small" sx={{ 
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': { 
                py: 1, 
                px: 2, 
                fontSize: '0.85rem',
                verticalAlign: 'middle',
                borderBottom: '1px solid rgba(224, 224, 224, 1)'
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                backgroundColor: '#f5f5f5',
                fontWeight: 600
              }
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 120 }}>Room Name</TableCell>
                  <TableCell sx={{ minWidth: 80 }}>Code</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Type</TableCell>
                  <TableCell sx={{ minWidth: 60, textAlign: 'center' }}>Cap.</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px' }}>
                      Beds
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dept.rooms.map(room => {
                  const roomBeds = bedsByRoom[room.roomId] || [];
                  const isRoomStandalone = isStandaloneRoom(room);
                  const isRoomAvailable = room.status === RoomStatus.AVAILABLE;
                  return (
                    <TableRow 
                      key={room.roomId} 
                      hover={isRoomStandalone && isRoomAvailable} 
                      sx={{
                        cursor: isRoomStandalone && isRoomAvailable ? 'pointer' : 'default',
                        '& .MuiTableCell-root': {
                          verticalAlign: 'middle'
                        }
                      }}
                      onClick={isRoomStandalone && isRoomAvailable ? () => handleAssignOrTransfer({ room }) : undefined}
                    >
                      <TableCell>{room.name || room.roomId}</TableCell>
                      <TableCell>{room.code || room.roomId}</TableCell>
                      <TableCell>
                        {room.type}
                        <Chip
                          size="small"
                          label={getRoomStatus(room)}
                          color={getRoomStatusColor(getRoomStatus(room))}
                          sx={{ ml: 1, height: 20 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {room.capacity || roomBeds.length || '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 0.5,
                          alignItems: 'center',
                          minHeight: '32px'
                        }}>
                          {isRoomStandalone ? (
                            <Chip 
                              label="Available" 
                              color="success" 
                              size="small" 
                              sx={{ 
                                height: 24, 
                                fontSize: '0.75rem',
                                '& .MuiChip-label': { px: 1 }
                              }} 
                            />
                          ) : roomBeds.length === 0 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                              0 Beds Available
                            </Typography>
                          ) : (
                            roomBeds.map(bed => {
                              const isAvailable = bed.status === BedStatus.AVAILABLE;
                              return (
                                <Chip
                                  key={bed.bedId}
                                  label={getBedDisplayName(bed, room)}
                                  color={getBedStatusColor(bed.status)}
                                  size="small"
                                  sx={{ 
                                    height: 24, 
                                    fontSize: '0.75rem',
                                    cursor: isAvailable ? 'pointer' : 'not-allowed', 
                                    opacity: isAvailable ? 1 : 0.6,
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                  onClick={isAvailable ? async (e) => { 
                                    e.stopPropagation(); 
                                    await handleAssignOrTransfer({ room, bed }); 
                                  } : undefined}
                                />
                              );
                            })
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export default BedAssignmentManager;